// Crypto Network Utilities
// Helper functions to identify crypto types and networks

export interface NetworkInfo {
  tokenName: string;
  networkName?: string;
  isNative: boolean;
}

/**
 * Get network information for a crypto wallet based on currency and network
 * @param currency - The currency code (e.g., "BTC", "ETH", "USDT_ERC20")
 * @param network - The network name (e.g., "bitcoin", "ethereum", "tron")
 * @returns NetworkInfo object with token name, network name, and whether it's native
 */
export function getNetworkInfo(currency: string, network: string): NetworkInfo {
  const currencyUpper = currency.toUpperCase();
  const networkLower = network.toLowerCase();

  // Bitcoin (BTC) - Native
  if (currencyUpper === 'BTC' || networkLower === 'bitcoin') {
    return {
      tokenName: 'Bitcoin (BTC)',
      isNative: true
    };
  }

  // Ethereum Network
  if (networkLower === 'ethereum') {
    // ETH - Native
    if (currencyUpper === 'ETH') {
      return {
        tokenName: 'Ethereum (ETH)',
        isNative: true
      };
    }
    // USDT ERC20 - Token
    if (currencyUpper === 'USDT_ERC20' || currencyUpper === 'USDT') {
      return {
        tokenName: 'USDT',
        networkName: 'Ethereum (ERC20)',
        isNative: false
      };
    }
    // USDC ERC20 - Token
    if (currencyUpper === 'USDC_ERC20' || currencyUpper === 'USDC') {
      return {
        tokenName: 'USDC',
        networkName: 'Ethereum (ERC20)',
        isNative: false
      };
    }
  }

  // Binance Smart Chain (BSC)
  if (networkLower === 'bnb' || networkLower === 'bsc' || networkLower === 'binance') {
    // BNB - Native
    if (currencyUpper === 'BNB') {
      return {
        tokenName: 'BNB (Binance Smart Chain)',
        isNative: true
      };
    }
    // USDT BEP20 - Token
    if (currencyUpper === 'USDT_BEP20' || currencyUpper === 'USDT') {
      return {
        tokenName: 'USDT',
        networkName: 'Binance Smart Chain (BEP20)',
        isNative: false
      };
    }
    // USDC BEP20 - Token
    if (currencyUpper === 'USDC_BEP20' || currencyUpper === 'USDC') {
      return {
        tokenName: 'USDC',
        networkName: 'Binance Smart Chain (BEP20)',
        isNative: false
      };
    }
  }

  // TRON Network
  if (networkLower === 'tron') {
    // TRX - Native
    if (currencyUpper === 'TRX') {
      return {
        tokenName: 'TRON (TRX)',
        isNative: true
      };
    }
    // USDT TRC20 - Token
    if (currencyUpper === 'USDT_TRC20' || currencyUpper === 'USDT') {
      return {
        tokenName: 'USDT',
        networkName: 'TRON (TRC20)',
        isNative: false
      };
    }
    // USDC TRC20 - Token
    if (currencyUpper === 'USDC_TRC20' || currencyUpper === 'USDC') {
      return {
        tokenName: 'USDC',
        networkName: 'TRON (TRC20)',
        isNative: false
      };
    }
  }

  // Solana Network
  if (networkLower === 'solana') {
    // SOL - Native
    if (currencyUpper === 'SOL') {
      return {
        tokenName: 'Solana (SOL)',
        isNative: true
      };
    }
    // USDT SPL - Token
    if (currencyUpper === 'USDT_SPL' || currencyUpper === 'USDT') {
      return {
        tokenName: 'USDT',
        networkName: 'Solana (SPL)',
        isNative: false
      };
    }
    // USDC SPL - Token
    if (currencyUpper === 'USDC_SPL' || currencyUpper === 'USDC') {
      return {
        tokenName: 'USDC',
        networkName: 'Solana (SPL)',
        isNative: false
      };
    }
  }

  // Fallback - treat as native if no network name can be determined
  return {
    tokenName: currencyUpper,
    isNative: true
  };
}

/**
 * Get the warning message key based on whether the crypto is native or a token
 * @param networkInfo - The network information object
 * @returns The i18n translation key for the warning message
 */
export function getWarningMessageKey(networkInfo: NetworkInfo): 'nativeToken' | 'token' {
  return networkInfo.isNative ? 'nativeToken' : 'token';
}
